import CountryCodeCookie from '~/shared/cookies/CountryCodeCookie';
import useCookie from '~/shared/hooks/useCookie';
import blockedCountryExceptions from '~/shared/utils/blockedCountryExceptions';
import { useCountryBlockedQuery } from './graphql/CountryBlocked.generated';
import useDidUpdate from '../../lifecycle/useDidUpdate';
import useAgentData from '../../useAgentData';
import useApolloError from '../../useApolloError';
import useInterval from '../../useInterval';
import useSite from '../../useSite';
import useIsLoggedIn from '../useIsLoggedIn';
import useLogout from '../useLogout';

const REFETCH_INTERVAL = 20_000;

const useCountryBlocked = () => {
  const { site } = useSite();
  const logout = useLogout();
  const { isCrawler } = useAgentData();
  const isLoggedIn = useIsLoggedIn();
  const { handleApolloError } = useApolloError();

  const [dismissBlockedCountry, setDismissBlockedCountry] = useCookie(
    'blocked-jurisdiction-dismissed',
  );

  const { data, loading, error, refetch } = useCountryBlockedQuery({
    variables: { site },
    fetchPolicy: 'no-cache',
  });

  useInterval(refetch, REFETCH_INTERVAL);
  const { blocked = false, countryCode = '' } =
    data?.userManagement?.currentCountryInfo ?? {};

  CountryCodeCookie.set(countryCode);

  const isCountryBlocked = isCrawler ? false : blocked;

  const handleDismiss = () => {
    setDismissBlockedCountry('true', {
      expires: new Date(Date.now() + 1_800_000),
    });
  };

  const isCountryException = blockedCountryExceptions.includes(countryCode);

  useDidUpdate(() => {
    if (isCountryBlocked && isLoggedIn && !isCountryException) {
      setDismissBlockedCountry(undefined);
      logout();
    }
  }, [isCountryBlocked]);

  if (error) {
    handleApolloError(error);
  }

  if (loading || error || !countryCode) {
    return {
      isCountryBlocked: false,
    };
  }

  return {
    isCountryBlocked,
    isCountryException,
    isLoggedIn,
    dismissBlockedCountry,
    handleDismiss,
  };
};

export default useCountryBlocked;
